// The calendar on a tablet, a desktop and a large screen (screens/CALENDAR.md):
// a row a week, the day names once at the top, a band under each row's dates
// for the league's weeks, and in each day its entries as cards that say in
// words what they are.
//
// EVERYTHING IS IN THE DAY IT HAPPENS ON, nothing beside the calendar: the
// calendar takes the screen's width, and a day's cards carry the names, the
// claims and the teams themselves (DIRECTION U30). A row grows with what its
// days hold, and the screen scrolls past two rows that will not fit.

import type { CalendarWeek } from '@fsp/domain';
import { bandsOf, dateOf, dayStart, weekdayOf, weekOfDay } from './days';
import { DayList } from './DayDetail';
import { dayLabelIn, weekLine, type Entry } from './words';

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const NOON = 12 * 3600 * 1000;

export function CalendarGrid({
  rows,
  zone,
  today,
  now,
  weeks,
  entriesOf,
  slateOf,
  numbers,
  roomy,
  base,
}: {
  rows: readonly (readonly string[])[];
  zone: string;
  today: string;
  now: number;
  weeks: readonly CalendarWeek[];
  entriesOf: (day: string) => readonly Entry[];
  /** How many of the sport's games start on a day, for the quiet count beside its date. */
  slateOf: (day: string) => number;
  /** Standard and up: the count is a number, which Simple leaves out. */
  numbers: boolean;
  /** Each player on a line of his own, with his position and opponent. */
  roomy: boolean;
  base: string;
}) {
  const first = rows[0] ?? [];
  return (
    <div className="fsp-cal-grid" data-rows={rows.length} data-roomy={roomy || undefined}>
      <div className="fsp-cal-dow" aria-hidden>
        {first.map((d) => (
          <span key={d}>{DOW[weekdayOf(d)]}</span>
        ))}
      </div>
      {rows.map((row) => (
        <div key={row[0]} className="fsp-cal-row">
          {/* THE DAYS' OWN GROUND, under the date, the band and the cards. */}
          {row.map((day, i) => (
            <div
              key={`cell-${day}`}
              className="fsp-cal-cell"
              data-col={i}
              data-today={day === today || undefined}
              data-past={day < today || undefined}
              aria-hidden
            />
          ))}
          {row.map((day, i) => {
            const date = dateOf(day);
            const games = numbers ? slateOf(day) : 0;
            return (
              <div
                key={`date-${day}`}
                className="fsp-cal-date"
                data-today={day === today || undefined}
                data-past={day < today || undefined}
                data-col={i}
                aria-hidden
              >
                <span className="fsp-cal-date-num">
                  {date === 1 ? monthDay(day, zone) : String(date)}
                </span>
                {day === today ? <span className="fsp-cal-date-today">Today</span> : null}
                {games > 0 ? (
                  <span className="fsp-cal-date-games">
                    {games} {games === 1 ? 'game' : 'games'}
                  </span>
                ) : null}
              </div>
            );
          })}
          {bandsOf(row, zone, weeks).map((b) => (
            <div
              key={`band-${row[0]}-${String(b.week.period)}`}
              className="fsp-cal-band"
              data-kind={b.week.kind}
              data-col={b.from}
              data-span={b.span}
              aria-hidden
            >
              <span className="fsp-cal-band-text">{weekLine(b.week)}</span>
            </div>
          ))}
          {row.map((day, i) => {
            const week = weekOfDay(day, zone, weeks);
            return (
              <DayList
                key={`cards-${day}`}
                col={i}
                entries={entriesOf(day)}
                now={now}
                today={day === today}
                zone={zone}
                base={base}
                numbers={numbers}
                roomy={roomy}
                label={`${dayLabelIn(dayStart(day, zone) + NOON, zone)}${
                  week === null ? '' : `, ${weekLine(week)}`
                }`}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
}

/** "Nov 1": the first of a month says which. */
function monthDay(day: string, zone: string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: zone,
  }).format(new Date(dayStart(day, zone) + NOON));
}
